import InputSearch from "@/components/input-search";
import { Popover, PopoverContent } from "@/components/ui/popover";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useListResources } from "@/hooks/list-resources.hook";
import { useResourceLibrary } from "@/hooks/resource-library.hook";
import {
  ListResourcesResponseContent,
  ListResourcesResponseContentType,
} from "@/requests/libraries/list-resources.request";
import { PopoverAnchor } from "@radix-ui/react-popover";
import { Book, Paperclip2, Warning2 } from "iconsax-react";
import { FC, useState } from "react";
import { useDebounce } from "use-debounce";

/**
 * The data that is passed to the onOpenChange function when the drawer is closed
 * This contains the data related to the changed resource (link)
 */
export type MiniLibraryOpenChangeData = null | ListResourcesResponseContent;

type MiniLibraryProps = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean, data: MiniLibraryOpenChangeData) => void;
};

const MiniLibrary: FC<MiniLibraryProps> = (props) => {
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] =
    useState<ListResourcesResponseContentType | null>(null);

  const handleOpenChange = (
    isOpen: boolean,
    data?: MiniLibraryOpenChangeData,
  ) => {
    if (isOpen) {
      props.onOpenChange(isOpen, null);
    } else {
      props.onOpenChange(false, data || null);
    }
  };

  const handleResourceClick = (resource: ListResourcesResponseContent) => {
    props.onOpenChange(false, resource);
  };

  const [debouncedQ] = useDebounce(q, 500);

  const { data: resourceLibrary } = useResourceLibrary();

  const { data: resourceList } = useListResources({
    companyId: resourceLibrary?.owner.id || "",
    resourceLibraryId: resourceLibrary?.id || "",
    q: debouncedQ,
    types: typeFilter ? [typeFilter] : [],
    size: 25,
  });

  return (
    <Popover open={props.isOpen} onOpenChange={handleOpenChange} modal={true}>
      <PopoverAnchor />
      <PopoverContent className="flex min-h-[250px] w-[500px] flex-col gap-4">
        <InputSearch
          id="mini-library-search"
          placeholder="Buscar recurso por nombre..."
          value={q}
          onChange={setQ}
        />

        <ToggleGroup
          variant="outline"
          type="single"
          className="justify-start"
          onValueChange={(value: ListResourcesResponseContentType) =>
            setTypeFilter(value)
          }
        >
          <ToggleGroupItem
            value="FILE"
            aria-label="Activar/desactivar filtro de archivos"
          >
            <Book size="16" />
            Documentos
          </ToggleGroupItem>
          <ToggleGroupItem
            value="LINK"
            aria-label="Activar/desactivar filtro de enlaces"
          >
            <Paperclip2 size="16" />
            Links
          </ToggleGroupItem>
        </ToggleGroup>

        <div className="flex max-h-60 flex-col gap-1 overflow-auto">
          {resourceList &&
            resourceList.content.length > 0 &&
            resourceList.content.map((resource) => (
              <div
                key={resource.id}
                className="flex cursor-pointer flex-row items-start gap-1 py-1 text-sm text-[#4F5D6D] hover:bg-secondary hover:text-primary"
                onClick={() => handleResourceClick(resource)}
              >
                {resource.type === "FILE" && (
                  <Book className="mr-1 inline-block shrink-0" size="14" />
                )}
                {resource.type === "LINK" && (
                  <Paperclip2
                    className="mr-1 inline-block shrink-0"
                    size="14"
                  />
                )}
                <div className="-mt-1">{resource.name}</div>
              </div>
            ))}

          {resourceList && resourceList.content.length === 0 && (
            <div className="my-4 flex flex-col items-center justify-center gap-2 text-center text-sm text-[#4F5D6D]">
              <Warning2 size="32" />
              <div>No hay resultados</div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default MiniLibrary;
